import lambforgeShot from 'url:../../../../assets/images/work/lambforge.webp';
import steelfabShot from 'url:../../../../assets/images/work/steelfab.webp';

import { SiteContent } from './site-content.types';

/**
 * The website's built-in content. A section saved in the admin replaces its
 * default (field by field); sections never saved use these.
 */
export const SITE_CONTENT_DEFAULTS: SiteContent = {
  hero: {
    kicker: 'Reactive Solutions · Software studio · Sofia',
    status: 'Available for new projects',
    headlineTop: 'Software',
    headlineBottom: 'that [reacts.]',
    lead:
      'We design, build and run digital products end to end, on **frameworks we wrote** and a **cloud platform we own**. ' +
      'One team from the first sketch to production. No hand-offs, no surprises.',
    primaryCta: 'Start a project',
    secondaryCta: 'See our work',
    facts: [
      { value: '292K+', label: 'npm downloads of our frameworks in the last 12 months' },
      { value: '150+', label: 'open-source repositories' },
      { value: '2019', label: 'Lambforge, our cloud platform, in production since' },
      { value: 'EU', label: 'built in Sofia, hosted in the EU' },
    ],
  },
  work: {
    heading: 'Shipped and [live].',
    lead:
      "We don't make mockups that never ship. Every project below runs in production on infrastructure we operate, " +
      'and you can open each one right now.',
    cases: [
      {
        id: 'steelfab',
        kind: 'Client',
        client: 'SteelFab · industrial equipment, Bulgaria',
        title: 'A catalogue that sells heavy machinery.',
        body:
          'Bilingual B2B storefront and admin panel for an industrial equipment supplier: a five-level product catalogue, ' +
          'CMS pages and PDF datasheets, server-rendered pages for search engines at the edge, and a GraphQL API cached by ' +
          'Cloudflare. Hosted on Lambforge.',
        tags: ['GraphQL', '@rhtml + Lit', 'Cloudflare Workers', 'R2 storage', 'Lambforge'],
        url: 'https://www.steelfab.bg',
        urlLabel: 'steelfab.bg',
        image: steelfabShot,
      },
      {
        id: 'lambforge',
        kind: 'Our product',
        client: 'Lambforge · cloud platform',
        title: 'We built our own cloud. Your backend runs on it.',
        body:
          'A serverless platform on Kubernetes that we build and run: GraphQL and REST lambdas, federation gateways, ' +
          'private Talos clusters, queues and cron, and AI agents that write, test and deploy code. It runs our ' +
          'clients’ products, this website included.',
        tags: ['Kubernetes', 'Fission (our fork)', 'Talos', 'AI agents + MCP', 'GraphQL federation'],
        url: 'https://lambforge.com',
        urlLabel: 'lambforge.com',
        image: lambforgeShot,
      },
      {
        id: 'legends',
        kind: 'Lab',
        client: 'Card game · real-time multiplayer',
        title: 'Real-time multiplayer, in a browser tab.',
        body:
          'A competitive card game with an authoritative engine: every move is validated on the server over NATS, ' +
          'pushed to both players through GraphQL subscriptions and rendered with PixiJS. Built as an Nx monorepo ' +
          'and deployed on Lambforge.',
        tags: ['PixiJS', 'GraphQL subscriptions', 'NATS', 'Nx monorepo', 'Lambforge'],
        url: 'https://legends.lambforge.com',
        urlLabel: 'Play it',
        image: '',
      },
    ],
  },
  stack: {
    heading: 'We own [every layer] under your product.',
    lead:
      'Most studios rent their stack. We wrote ours. The frameworks, the API layer, the serverless runtime and the ' +
      'clusters it runs on are all built and operated by the same people who write your product.',
    layers: [
      {
        name: 'Your product',
        what: 'Web apps, admin panels, APIs, real-time games, AI features',
        ours: 'yours',
        owner: 'you',
      },
      {
        name: 'Frontend',
        what: '@rhtml web components + @rxdi dependency injection: light, fast UIs with no framework tax',
        ours: 'ours · MIT',
        owner: 'oss',
      },
      {
        name: 'API',
        what: '@gapi GraphQL: typed schemas, federation, subscriptions, RabbitMQ and NATS',
        ours: 'ours · MIT',
        owner: 'oss',
      },
      {
        name: 'Runtime',
        what: 'Lambforge: serverless lambdas, autoscaling, queues, cron, edge cache, AI agents',
        ours: 'ours · platform',
        owner: 'platform',
      },
      {
        name: 'Infrastructure',
        what: 'Kubernetes on Talos Linux, shared or private clusters, EU data centres',
        ours: 'ours · operated',
        owner: 'ops',
      },
    ],
    footnote: 'four of five layers are ours',
    promises: [
      {
        title: 'No black boxes',
        body:
          'When something misbehaves we can trace it from a button click down to the cluster and fix it in the right ' +
          'layer, instead of working around it.',
      },
      {
        title: 'No lock-in',
        body:
          'The frameworks are MIT-licensed and your code is yours. Run it on Lambforge, on your own cluster, or anywhere ' +
          'Node.js runs.',
      },
      {
        title: 'Honest running costs',
        body:
          'We operate the cloud ourselves, so we can show you exactly what your product costs to run, down to the ' +
          'pod-minute.',
      },
    ],
  },
  services: {
    heading: 'From first sketch to [running in production].',
    lead:
      'Pick one, or hand us the whole thing. Either way the same small senior team designs it, builds it and keeps it ' +
      'running.',
    services: [
      {
        title: 'Product engineering',
        body: 'Web apps, customer portals, catalogues and admin panels, designed and built end to end by one team.',
        items: ['UX & interface design', 'Web-component frontends', 'GraphQL APIs', 'Admin & CMS'],
        accent: 'signal',
      },
      {
        title: 'Cloud & platform',
        body: 'Your backend on Lambforge, or on a private Kubernetes cluster that we set up and run for you.',
        items: ['Serverless APIs', 'Private clusters', 'CI/CD pipelines', 'Monitoring & cost control'],
        accent: 'plasma',
      },
      {
        title: 'AI agents & automation',
        body: 'Agents that work inside your product: they search your data, call your APIs and act within guardrails you set.',
        items: ['MCP servers for your API', 'Search over your documents', 'Workflow automation', 'Model cost control'],
        accent: 'ion',
      },
      {
        title: 'Rescue & modernisation',
        body: 'Legacy code, slow pages, leaking secrets or a cloud bill nobody understands. We stabilise it, then move it forward.',
        items: [
          'Performance & caching',
          'Security & secrets audit',
          'Platform migrations',
          'Refactoring to a modern stack',
        ],
        accent: 'ember',
      },
    ],
  },
  process: {
    heading: 'A pipeline you can [watch run].',
    steps: [
      {
        op: 'discover()',
        title: 'Discover',
        body:
          'We learn your business, your users and the constraints, then agree on scope, priorities and a budget you can ' +
          'plan around.',
      },
      {
        op: 'prototype()',
        title: 'Prototype',
        body: 'A clickable, running prototype on a real URL, so decisions are made on working software instead of slides.',
      },
      {
        op: 'build()',
        title: 'Build',
        body: 'Short iterations with a live preview environment for every change. You see progress weekly, not at the end.',
      },
      {
        op: 'run()',
        title: 'Run & evolve',
        body: 'We host, monitor and improve it on Lambforge. Costs, uptime and changes stay visible to you.',
      },
    ],
  },
  openSource: {
    heading: 'The frameworks [other teams] build on.',
    lead:
      '@rxdi, @rhtml and @gapi are our own TypeScript frameworks for web components, dependency injection and GraphQL, ' +
      'MIT-licensed and downloaded close to 300,000 times in the last year. Your product is built on tools whose ' +
      'authors answer your call.',
    packagesNote: 'downloads · last 12 months',
    packages: [
      { name: '@rxdi/core', what: 'Dependency injection & modules', downloads: 55925 },
      { name: '@gapi/core', what: 'GraphQL server framework', downloads: 41460 },
      { name: '@rxdi/graphql', what: 'GraphQL schema & resolvers', downloads: 40688 },
      { name: '@gapi/cli', what: 'Project CLI', downloads: 36789 },
      { name: '@rxdi/graphql-client', what: 'GraphQL client for the browser', downloads: 33519 },
      { name: '@rxdi/router', what: 'Router for web components', downloads: 28263 },
      { name: '@rxdi/lit-html', what: 'Web components on Lit', downloads: 26057 },
      { name: '@gapi/gcli', what: 'Lambforge CLI', downloads: 19953 },
    ],
    githubUrl: 'https://github.com/Stradivario',
    npmUrl: 'https://www.npmjs.com/package/@gapi/core',
  },
  contact: {
    heading: 'Got something that needs to [react?]',
    email: 'support@reactivum.com',
    phone: '+359 876 667 537',
    city: 'Sofia, Bulgaria',
    next: [
      'You tell us what you want to build, fix or move.',
      'We come back with questions or a first plan.',
      'We agree on scope and budget before any code is written.',
    ],
  },
  company: {
    name: 'Reactive Solutions LTD',
    vat: 'BG205455032',
    address: '9 Leonardo da Vinci St, 1124 Sofia, Bulgaria',
    founded: 2019,
    tagline: 'A software studio from Sofia. We build products, the frameworks under them, and the cloud they run on.',
  },
  seo: {
    title: 'Reactive Solutions - Software studio from Sofia',
    description:
      'We design, build and run digital products end to end, on our own open-source frameworks (@rxdi, @rhtml, @gapi) ' +
      'and our own cloud platform, Lambforge.',
  },
};
